import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAgentCopy } from '../lib/agentCopy.js';
import { AgentProfileLink } from '../components/AgentProfileLink.js';
import { GoogleCalendarConnection } from '../components/GoogleCalendarConnection.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  Bell,
  Check,
  ChevronDown,
  Triangle,
  CreditCard,
  Globe,
  LogOut,
  Moon,
  Palette,
  Pencil,
  Settings as SettingsIcon,
  Smartphone,
  ShieldCheck,
  Sun,
  Trash2,
  UserCircle,
} from 'lucide-react';
import {
  CURRENCIES,
  LOCALES,
  LOCALE_LABELS,
  updateProfileSchema,
  type PlanLimits,
  type PlanUsage,
  type ReportSummary,
  type SessionResponse,
  type Subscription,
  type User,
} from '@worki/shared';
import { RequestError, api, billingApi } from '../lib/api.js';
import { useAuthenticated, useSession } from '../lib/session.js';
import { useForm } from '../lib/useForm.js';
import { changeLocale, currentLocale } from '../i18n.js';
import { formatDate, money } from '../lib/format.js';
import { cn } from '../lib/cn.js';
import {
  THEMES,
  DEFAULT_LIST_GRADIENT,
  DEFAULT_BORDER_INTENSITY,
  applyDarkMode,
  currentBorderIntensity,
  currentInvertColors,
  currentDarkPreference,
  currentListGradient,
  currentThemeId,
  previewLook,
  restoreStoredLook,
  setListGradient,
  setBorderIntensity,
  setInvertColors,
  setTheme,
} from '../lib/theme.js';
import { Button, ErrorNotice, Field, Input, Select, Spinner, Textarea } from '../components/ui.js';
import { ConfirmDialog, Modal } from '../components/Modal.js';
import { PlanDialog } from '../components/PlanDialog.js';
import { useToast } from '../components/Toast.js';
import { isPlayBuild } from '../lib/config.js';
import { AppDownloadCard } from '../components/AppDownloadCard.js';
import { setNavigationMode, useNavigationMode } from '../lib/navigationLayout.js';

/**
 * One card open at a time, and none of them open to begin with: the screen
 * answers "what can I change here" before it answers any one of them.
 */
type SectionId = 'plan' | 'subscriber' | 'settings' | 'reports' | 'admin';

/**
 * Three cards left this screen, and each for its own reason.
 *
 * **VAT.** The rate and the "prices include VAT" switch are now set on the
 * quote being written, where the number they change is on screen next to them.
 * Setting them here meant leaving the quote, changing a business-wide default,
 * and coming back — for a decision that is usually about *this* customer.
 *
 * **Quote branding.** It held a logo upload and a quote footer. The logo was
 * already uploaded from the gallery, at `/auth/profile/logo`, the same endpoint
 * — so the product asked for one picture in two places and showed the result in
 * one. The footer moved to the business card, next to the other things printed
 * on a quote.
 *
 * **Receipts.** It said, at length, that there was nothing to download. Worki
 * issues no receipt of its own and has no endpoint that lists one, and a card
 * whose entire content is an apology is not a card.
 */

/** The counter and the cap it is measured against, as on the billing screen. */
const PLAN_ROWS: Array<{ usageKey: keyof PlanUsage; limitKey: keyof PlanLimits }> = [
  { usageKey: 'quotesThisMonth', limitKey: 'quotesPerMonth' },
  { usageKey: 'voiceActionsThisMonth', limitKey: 'voiceActionsPerMonth' },
  { usageKey: 'galleries', limitKey: 'galleries' },
  { usageKey: 'connectedMailAccounts', limitKey: 'connectedMailAccounts' },
];

/** Severity, not the alphabet: a failed charge must not look like a trial. */
const STATUS_PILL: Record<Subscription['status'], string> = {
  trialing: 'bg-info/10 text-info',
  active: 'bg-success/10 text-success',
  past_due: 'bg-warning/10 text-warning',
  cancelled: 'bg-muted text-muted-foreground',
  expired: 'bg-destructive/10 text-destructive',
};

/**
 * AUTH-004 — profile, business details, and the branding used on quote PDFs.
 *
 * Composed after the reference account screen: an identity header over an
 * accordion where one card is open at a time, and a card of fields is read-only
 * until its pencil is pressed. Every control is wired to something real, which
 * is where this parts company with the reference in three places:
 *
 * - The plan card reads the live subscription, its caps and its usage, and its
 *   cancel button is the real `POST /billing/cancel`, not a toast.
 * - The receipts card has no list. The reference hard-codes six months of
 *   invoices; this server issues none and has no endpoint to ask, so the card
 *   says what is true instead of inventing rows and a download button.
 * - There is no "customer service" card and no "delete subscription" button.
 *   Both are toasts in the reference, and there is no support inbox or account
 *   deletion endpoint here to point them at.
 *
 * The Worki-only sections the reference never had — the virtual number, VAT, the
 * follow-up window, quote branding — are kept, in the same visual language.
 */
export function ProfilePage() {
  const navigationMode = useNavigationMode();
  const { t } = useTranslation();
  const locale = currentLocale();
  const toast = useToast();
  const session = useAuthenticated();
  const { refresh, logout } = useSession();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState<SectionId | null>(() => typeof window!=='undefined' && new URLSearchParams(window.location.search).get('calendarConfirm')==='1' ? 'settings' : null);
  const [editing, setEditing] = useState(false);
  const [themeId, setThemeId] = useState(currentThemeId);
  const [gradient, setGradient] = useState(currentListGradient);
  const [dark, setDark] = useState(currentDarkPreference);
  const [borderIntensity, setBorderIntensityDraft] = useState(currentBorderIntensity);
  const [inverted, setInverted] = useState(currentInvertColors);
  const [settingsLocale, setSettingsLocale] = useState(currentLocale);
  const [settingsDirty, setSettingsDirty] = useState(false);
  /* The settings tab now holds three fields that live on the server, so its one
     save button has two halves. This says whether the second half has anything
     to do — without it every press of it would PATCH the profile. */
  const [profileDirty, setProfileDirty] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState('');

  /* 1.42 — the accent, the mode and the two sliders show the moment they are
     touched, so a person sees what the save button would keep. Nothing here
     stores anything: that is the save button's alone. What was tried and not
     kept is put back when the screen goes away, and what was kept is already
     what is stored. Inversion follows the same preview and save behavior. */
  useEffect(() => {
    previewLook({ themeId, dark, gradient, borderIntensity, inverted });
  }, [themeId, dark, gradient, borderIntensity, inverted]);
  useEffect(() => () => restoreStoredLook(), []);

  const form = useForm(updateProfileSchema, {
    name: session.user.name,
    businessName: session.business.name,
    businessEmail: session.business.email ?? '',
    phone: session.business.phone ?? '',
    address: session.business.address ?? '',
    taxId: session.business.taxId ?? '',
    currency: session.business.currency,
    slogan: session.business.slogan ?? '',
    quoteFooter: session.business.quoteFooter ?? '',
    virtualNumber: session.user.virtualNumber ?? '',
    // No VAT here. The rate is Worki's, one number for the whole platform, and
    // whether prices include it is decided per quote — both used to be seeded
    // into this form, where the schema had already stopped accepting them.
    quoteReminderHours: session.business.quoteReminderHours,
    signatureReminderHours: session.business.signatureReminderHours,
  });

  const billing = useQuery({ queryKey: ['billing'], queryFn: () => billingApi.summary() });

  /* Whether the signed-in user is Worki staff. The desktop rail asks the same
     question under the same query key, so the two share one answer per session;
     this card is what a phone has instead of that row. `/platform/me` answers
     everyone — the rest of module 13 answers 404 to non-staff, which a menu
     cannot tell apart from the API being down. */
  const platform = useQuery({
    queryKey: ['platform', 'me'],
    queryFn: () => api.get<{ isPlatformAdmin: boolean }>('/platform/me'),
    staleTime: Infinity,
  });
  const isPlatformAdmin = platform.data?.isPlatformAdmin ?? false;

  const save = useMutation({
    mutationFn: (payload: unknown) => api.patch<SessionResponse>('/auth/profile', payload),
    onSuccess: () => {
      refresh();
      setEditing(false);
      toast.success(t('profile.saved'));
    },
  });

  /*
   * TEL-001 — buying a line costs money every month, so it asks first, and the
   * button only exists when the server actually has a carrier configured.
   * The bought number is written straight into the field so a save that follows
   * does not overwrite it with the value the form loaded with.
   */
  const [provisionOpen, setProvisionOpen] = useState(false);
  const provision = useMutation({
    mutationFn: () => api.post<User>('/calls/virtual-number', {}),
    onSuccess: (user) => {
      form.setValue('virtualNumber', user.virtualNumber ?? '');
      refresh();
      toast.success(t('profile.provisionNumberDone'));
    },
    onError: (error: unknown) =>
      toast.error(t(error instanceof RequestError ? error.code : 'errors.unknown')),
  });

  /*
   * ACCT-DEL — nothing here is recoverable, so the address is typed out rather
   * than taken from the session, and the server checks it again for itself.
   * On success the session is dropped locally too: the cookie is already gone,
   * but a native shell keeps its bearer token until `logout` clears it, and the
   * route guard is what carries the person off this screen.
   */
  const deleteAccount = useMutation({
    mutationFn: (email: string) => api.post<{ deletedBusiness: boolean }>('/auth/account/delete', { email }),
    onSuccess: async () => {
      setDeleteOpen(false);
      toast.success(t('profile.deleteAccountDone'));
      await logout();
    },
    onError: (error: unknown) =>
      toast.error(t(error instanceof RequestError ? error.code : 'errors.unknown')),
  });

  const [plansOpen, setPlansOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const cancelPlan = useMutation({
    mutationFn: () => billingApi.cancel(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
      toast.success(t('billing.cancelDone'));
    },
    onError: (error: unknown) =>
      toast.error(t(error instanceof RequestError ? error.code : 'errors.unknown')),
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void form.submit(async (data) => {
      await save.mutateAsync(data);
    });
  };

  const toggle = (id: SectionId) => {
    setOpen((prev) => (prev === id ? null : id));
    // Leaving a card mid-edit and coming back to it read-only is less surprising
    // than finding another card already open in edit mode.
    setEditing(false);
  };

  /*
   * These are plain functions and not components: a component defined during
   * render is a new type on every keystroke, which would remount the field being
   * typed into and lose focus.
   */
  const card = (
    id: SectionId,
    label: string,
    icon: ReactNode,
    body: ReactNode,
    action?: ReactNode,
  ) => (
    <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
      <button
        type="button"
        onClick={() => toggle(id)}
        aria-expanded={open === id}
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-muted/50 transition-colors"
      >
        <span className="flex items-center gap-3 text-sm font-semibold text-foreground">
          {icon}
          {label}
        </span>
        {id === 'plan' ? (
          <Triangle className={cn('h-3 w-3 fill-current text-muted-foreground transition-transform', open === id ? 'rotate-180' : 'rotate-90 rtl:-rotate-90')} />
        ) : (
          <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', open === id && 'rotate-180')} />
        )}
      </button>
      {open === id && (
        <div className="px-4 pb-4 pt-3 border-t border-border space-y-3">
          {action}
          {body}
        </div>
      )}
    </div>
  );

  /** The reference's "all details / edit" row, above a card's fields. */
  const editRow = (label: string) => (
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold text-muted-foreground">{label}</p>
      <button
        type="button"
        onClick={() => setEditing((v) => !v)}
        className="text-xs text-primary hover:underline flex items-center gap-1"
      >
        <Pencil className="h-3 w-3" />
        {editing ? t('profile.editDone') : t('common.edit')}
      </button>
    </div>
  );

  const saveRow = () =>
    editing && (
      <div className="pt-1">
        <Button type="submit" variant="primary" loading={form.submitting}>
          {t('common.save')}
        </Button>
      </div>
    );

  /**
   * A field is a label over its value, and the value is text until the card is in
   * edit mode. `children` renders the editor for anything that is not a plain
   * text input — a select, a number, a checkbox.
   */
  const field = (
    name: string,
    label: string,
    opts: { ltr?: boolean; display?: string; children?: ReactNode } = {},
  ) => {
    const raw = form.values[name];
    const display = opts.display ?? (raw === '' || raw === null || raw === undefined ? '—' : String(raw));
    const error = form.errors[name];
    return (
      <div key={name} className="space-y-1">
        {editing ? (
          <Field
            label={label}
            htmlFor={`p-${name}`}
            {...(error ? { error: t(error) } : {})}
          >
            {opts.children ?? (
              <Input
                id={`p-${name}`}
                {...(opts.ltr ? { dir: 'ltr' as const } : {})}
                value={String(raw ?? '')}
                invalid={Boolean(error)}
                onChange={(event) => form.setValue(name, event.target.value)}
              />
            )}
          </Field>
        ) : (
          <>
            <p className="text-[11px] text-muted-foreground">{label}</p>
            <p className="text-sm font-medium text-foreground truncate" {...(opts.ltr ? { dir: 'ltr' } : {})}>
              {display}
            </p>
          </>
        )}
      </div>
    );
  };

  const grid = (children: ReactNode) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;

  const planBody = () => {
    if (billing.isLoading) {
      return (
        <div className="py-4 flex justify-center">
          <Spinner />
        </div>
      );
    }
    if (!billing.data) return <ErrorNotice code="errors.unknown" />;
    const { subscription, limits, usage, plans, currency, paymentsEnabled, writable } = billing.data;
    const plan = plans.find((p) => p.id === subscription.plan);
    /*
     * This is now the only place a subscription can be cancelled, so it has to
     * cover the state an owner most wants out of: a charge that keeps failing.
     * `past_due` is included for that reason; a subscription already cancelled
     * or expired has nothing left to stop.
     */
    const cancellable =
      paymentsEnabled &&
      !subscription.cancelledAt &&
      (subscription.status === 'active' ||
        subscription.status === 'trialing' ||
        subscription.status === 'past_due');
    const period =
      subscription.cancelledAt && subscription.currentPeriodEnd
        ? t('billing.cancelledAt', { date: formatDate(subscription.currentPeriodEnd, locale) })
        : subscription.status === 'trialing' && subscription.trialEndsAt
          ? t('billing.trialEndsAt', { date: formatDate(subscription.trialEndsAt, locale) })
          : subscription.currentPeriodEnd
            ? t('billing.renewsAt', { date: formatDate(subscription.currentPeriodEnd, locale) })
            : null;

    return (
      <>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-bold text-foreground">{t(`billing.plans.${subscription.plan}`)}</p>
            <p className="text-xs text-muted-foreground">
              {plan && (
                <span className="ltr-nums">
                  {money(plan.monthlyPriceAgorot / 100, currency, locale)} {t('billing.perMonth')}
                </span>
              )}
              {plan && period ? ' · ' : ''}
              {period}
            </p>
          </div>
          <span className={cn('text-[11px] px-2.5 py-1 rounded-full font-medium shrink-0', STATUS_PILL[subscription.status])}>
            {t(`billing.status.${subscription.status}`)}
          </span>
        </div>

        {/* The shell's read-only banner steps aside on this screen, so the
            sentence it carries has to be here: a status pill reading "awaiting
            payment" says what the state is called, not that the account has
            stopped accepting edits or what to do about it. */}
        {(!writable || subscription.status === 'past_due') && (
          <p className="text-xs rounded-lg border border-warning/40 bg-warning/10 px-3 py-2">
            {t(subscription.status === 'past_due' ? 'billing.pastDueNotice' : 'billing.inactiveNotice')}
          </p>
        )}

        <PlanQuota limits={limits} usage={usage} />

        {subscription.cardLast4 && (
          <p className="text-xs text-muted-foreground ltr-nums">
            {t('billing.card', { last4: subscription.cardLast4 })}
          </p>
        )}

        {/* 44px on a phone, where both of these are thumb targets. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setPlansOpen(true)}
            className="h-11 rounded-xl border border-border bg-card text-sm font-medium flex items-center justify-center gap-2 hover:bg-muted transition-colors"
          >
            <CreditCard className="h-4 w-4" />
            {t('billing.changePlan')}
          </button>
          {cancellable && (
            <button
              type="button"
              onClick={() => setCancelOpen(true)}
              disabled={cancelPlan.isPending}
              className="h-11 rounded-xl border border-destructive/40 text-destructive text-sm font-medium hover:bg-destructive/10 transition-colors disabled:opacity-60"
            >
              {t('billing.cancel')}
            </button>
          )}
        </div>
      </>
    );
  };

  const settingsBody = () => (
    <>
      <GoogleCalendarConnection />
      <div className="space-y-2">
        <p className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
          <Palette className="h-3.5 w-3.5" />
          {t('theme.title')}
        </p>
        <p className="text-[11px] text-muted-foreground">{t('theme.subtitle')}</p>
        <div className="flex flex-wrap gap-2.5">
          {THEMES.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                setThemeId(option.id);
                setSettingsDirty(true);
              }}
              aria-label={t(option.labelKey)}
              aria-pressed={themeId === option.id}
              title={t(option.labelKey)}
              className={cn(
                // The hairline is on all 26 and not only the chosen one: the
                // fan runs from black to white, so at either end of it a disc
                // painted straight onto the card has no edge at all. Measured
                // in dark mode, "black" came to 1.24:1 against the card; the
                // ring composites over the swatch, so it reads whichever way
                // the mode falls.
                'h-9 w-9 rounded-full border border-foreground/55 transition-transform hover:scale-110 ring-offset-2 ring-offset-card',
                themeId === option.id && 'ring-2 ring-foreground/40 scale-110',
              )}
              style={{ background: option.swatch }}
            >
              {themeId === option.id && <Check className="h-4 w-4 text-white mx-auto" />}
            </button>
          ))}
        </div>
      </div>

      {/* How much of that accent the lists carry. A slider and not a switch:
          how much colour a list holds before it stops reading as a list is a
          matter of taste and of the screen it is read on. Zero is the flat row
          this app had until now. */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-semibold text-muted-foreground">{t('profile.listGradient')}</p>
          <span className="text-xs font-semibold text-foreground ltr-nums">{gradient}</span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={gradient}
          aria-label={t('profile.listGradient')}
          onChange={(event) => {
            const next = Number(event.target.value);
            setGradient(next);
            setSettingsDirty(true);
          }}
          className="range-slider"
          style={{ '--range-fill': `${gradient}%` } as CSSProperties}
        />
        <p className="text-[11px] text-muted-foreground">{t('profile.listGradientHint')}</p>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <label htmlFor="profile-border-intensity" className="text-xs font-semibold text-muted-foreground">{t('profile.borderIntensity')}</label>
          <span className="text-xs font-semibold text-foreground ltr-nums">{borderIntensity}</span>
        </div>
        <input
          id="profile-border-intensity"
          type="range" min={0} max={100} step={5} value={borderIntensity}
          onChange={(event) => {
            setBorderIntensityDraft(Number(event.target.value));
            setSettingsDirty(true);
          }}
          className="range-slider"
          style={{ '--range-fill': `${borderIntensity}%` } as CSSProperties}
        />
      </div>
      <label className="flex items-center justify-between gap-3 text-xs font-semibold text-muted-foreground">
        {t('profile.invertColors')}
        <input type="checkbox" checked={inverted} className="h-4 w-4 accent-primary" onChange={(event) => {
          setInverted(event.target.checked);
          setSettingsDirty(true);
        }} />
      </label>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs font-semibold text-muted-foreground">{t('profile.appearance')}</p>
        <div className="flex rounded-xl bg-muted p-1 gap-0.5">
          {[
            { value: false, label: t('profile.light'), icon: <Sun className="h-3.5 w-3.5" /> },
            { value: true, label: t('profile.dark'), icon: <Moon className="h-3.5 w-3.5" /> },
          ].map((mode) => (
            <button
              key={String(mode.value)}
              type="button"
              onClick={() => {
                setDark(mode.value);
                setSettingsDirty(true);
              }}
              aria-pressed={dark === mode.value}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all',
                dark === mode.value ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground',
              )}
            >
              {mode.icon}
              {mode.label}
            </button>
          ))}
        </div>
      </div>

      {/* The language is a per-device preference like the two above it, so it
          belongs here and not only in the navigation rail. Each option carries
          its own endonym: someone who cannot read the current interface can
          still find their language. */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs font-semibold text-muted-foreground">{t('common.language')}</p>
        <div className="flex rounded-xl bg-muted p-1 gap-0.5">
          {LOCALES.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                setSettingsLocale(option);
                setSettingsDirty(true);
              }}
              aria-pressed={settingsLocale === option}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                settingsLocale === option ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground',
              )}
            >
              {LOCALE_LABELS[option]}
            </button>
          ))}
        </div>
      </div>
      {/* 5.5 — the settings tab is where a person looks for a setting, so the
          two follow-up windows and the currency are here rather than filed
          under the subscriber's contact details behind a pencil. */}
      <BusinessPreferences
        values={{
          currency: String(form.values.currency ?? 'ILS'),
          quoteReminderHours: String(form.values.quoteReminderHours ?? ''),
          signatureReminderHours: String(form.values.signatureReminderHours ?? ''),
        }}
        errors={{
          ...(form.errors.quoteReminderHours ? { quoteReminderHours: form.errors.quoteReminderHours } : {}),
          ...(form.errors.signatureReminderHours
            ? { signatureReminderHours: form.errors.signatureReminderHours }
            : {}),
        }}
        onChange={(name, value) => {
          form.setValue(name, value);
          setProfileDirty(true);
        }}
      />

      <div className="flex flex-wrap gap-2 pt-2">
        <Button type="button" variant="primary" disabled={!settingsDirty && !profileDirty} onClick={() => {
          setTheme(themeId);
          setInvertColors(inverted);
          applyDarkMode(dark);
          setListGradient(gradient);
          setBorderIntensity(borderIntensity);
          if (settingsLocale !== locale) void changeLocale(settingsLocale);
          setSettingsDirty(false);
          /*
           * The colours, the language and the two sliders are this device's and
           * are already applied by the lines above. The three below them belong
           * to the business, so the same press has to reach the server — and the
           * announcement comes from the mutation, which is also what keeps the
           * button live when a number was rejected.
           */
          if (profileDirty) {
            void form.submit(async (data) => {
              await save.mutateAsync(data);
              setProfileDirty(false);
            });
            return;
          }
          toast.success(t('profile.settingsSaved'));
        }}>
          <Check className="h-4 w-4" />
          {t('profile.saveSettings')}
        </Button>
        <Button type="button" onClick={() => {
          setThemeId('purple');
          setGradient(DEFAULT_LIST_GRADIENT);
          setBorderIntensityDraft(DEFAULT_BORDER_INTENSITY);
          setDark(false);
          setInverted(false);
          setSettingsDirty(true);
        }}>
          {t('profile.resetColors')}
        </Button>
      </div>
      <fieldset className="border-t border-border pt-4 space-y-2">
        <legend className="text-sm font-semibold">{t('completion.navigation')}</legend>
        <div className="flex gap-2">{(['sidebar', 'bottom'] as const).map(mode => <Button type="button" key={mode} aria-pressed={navigationMode === mode} variant={navigationMode === mode ? 'primary' : 'secondary'} onClick={() => setNavigationMode(mode)}>{t(mode === 'sidebar' ? 'completion.sidebar' : 'completion.bottomNav')}</Button>)}</div>
      </fieldset>
      <section className="border-t border-border pt-5 space-y-3">
        <h3 className="text-sm font-semibold text-destructive">{t('profile.deleteAccount')}</h3>
        <p className="text-xs text-muted-foreground">{t('profile.deleteAccountBody')}</p>
        <Link to="/delete-account" className="text-xs font-medium text-primary hover:underline inline-block">{t('profile.deleteAccountLearnMore')}</Link>
        <Button type="button" onClick={() => { setDeleteEmail(''); setDeleteOpen(true); }}><Trash2 className="h-4 w-4" />{t('profile.deleteAccount')}</Button>
      </section>
    </>
  );

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl mx-auto space-y-4 animate-fade-up" noValidate>
      <IdentityHeader name={session.business.name} slogan={session.business.slogan} />
      <AppDownloadCard />
      <AgentProfileLink />

      {form.formError && <ErrorNotice code={form.formError} />}

      {card('plan', t('profile.plan'), <Check className="h-4 w-4 text-primary" />, planBody())}

      {card(
        'subscriber',
        t('profile.subscriber'),
        <UserCircle className="h-4 w-4 text-primary" />,
        <>
          {grid(field('name', t('auth.name')))}
          {/*
           * TEL-001 — inbound call webhooks are matched on this number, so the
           * call log stays empty until it is filled in. A number you already own
           * is typed in here; the button next to it asks the carrier for a new
           * one, and is absent on servers with no carrier configured.
           */}
          <div className="space-y-1">
            <p className="text-[11px] text-muted-foreground">{t('profile.virtualNumber')}</p>
            <div className="flex items-center gap-2">
              {editing ? (
                <Input
                  id="p-virtualNumber"
                  dir="ltr"
                  value={String(form.values.virtualNumber ?? '')}
                  onChange={(event) => form.setValue('virtualNumber', event.target.value)}
                />
              ) : (
                <p className="text-sm font-medium text-foreground grow" dir="ltr">
                  {String(form.values.virtualNumber || '—')}
                </p>
              )}
              {session.features.telephonyProvisioning && !form.values.virtualNumber && (
                <Button
                  type="button"
                  variant="secondary"
                  loading={provision.isPending}
                  onClick={() => setProvisionOpen(true)}
                >
                  {t('profile.provisionNumber')}
                </Button>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">{t('profile.virtualNumberHint')}</p>
          </div>
          {/* The business's details follow the person's rather than sitting in
              a card of their own: one subscriber signs up, and splitting the
              identity in two only asked which half a field belonged to. */}
          {grid(
            <>
              {field('businessName', t('auth.businessName'))}
              {field('slogan', t('profile.slogan'))}
              {field('taxId', t('profile.taxId'), { ltr: true })}
              {field('address', t('profile.address'))}
              {field('phone', t('customer.phone'), { ltr: true })}
              {field('businessEmail', t('customer.email'), { ltr: true })}
            </>,
          )}
          {/* Printed at the foot of every quote and PDF, so it belongs with the
              rest of what a customer reads about this business. */}
          {field('quoteFooter', t('profile.quoteFooter'), {
            children: (
              <Textarea
                id="p-quoteFooter"
                value={String(form.values.quoteFooter ?? '')}
                onChange={(event) => form.setValue('quoteFooter', event.target.value)}
              />
            ),
          })}
          {/* The currency and the two follow-up windows used to close this card.
              They are preferences, not contact details, so they are on the
              settings tab now — see `BusinessPreferences`. */}
          {saveRow()}
        </>,
        editRow(t('profile.allDetails')),
      )}

      {card('settings', t('profile.settings'), <SettingsIcon className="h-4 w-4 text-primary" />, settingsBody())}

      {/*
        The headline figures, and a way through to the screen that explains
        them. Not a second reports screen: the four numbers here are the ones
        an owner checks without meaning to sit down with the data, and anything
        past that is one tap away.
      */}
      {card(
        'reports',
        t('profile.reports'),
        <BarChart3 className="h-4 w-4 text-primary" />,
        <ReportsSnapshot />,
      )}

      {/*
        The gallery, which the phone's bottom bar used to carry. It is a screen
        an owner fills in once and then opens rarely, so it gave its slot to the
        history of what was sent and is reached from here instead — a row and
        not a card, because there is nothing to unfold: it is another screen.
      */}
      <Link
        to="/inquiries"
        className="rounded-2xl border border-border/60 bg-card px-4 py-3.5 flex items-center gap-3 text-sm font-semibold text-foreground hover:bg-muted/50 transition-colors"
      >
        <Smartphone className="h-4 w-4 text-primary" />
        {t('shell.navLeads')}
      </Link>
      <Link
        to="/mini-site"
        className="rounded-2xl border border-border/60 bg-card px-4 py-3.5 flex items-center gap-3 text-sm font-semibold text-foreground hover:bg-muted/50 transition-colors"
      >
        <Globe className="h-4 w-4 text-primary" />
        {t('shell.navGallery')}
      </Link>

      {/*
        Worki's own staff only. `/platform/me` answers everyone, which is what
        makes it safe to ask from here: the rest of module 13 answers 404 to
        anyone else, and a menu cannot tell that apart from the API being down.
        The card is not the gate — the API is — it only avoids showing a door
        that will not open.
      */}
      <Link to="/customer-payments" className="rounded-2xl border border-border/60 bg-card px-4 py-3.5 flex items-center gap-3 text-sm font-semibold text-foreground hover:bg-muted/50 transition-colors">
        <CreditCard className="h-4 w-4 text-primary" />{t('customerPayments.title')}
      </Link>
      {isPlatformAdmin &&
        card(
          'admin',
          t('profile.admin'),
          <ShieldCheck className="h-4 w-4 text-primary" />,
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">{t('profile.adminBody')}</p>
            <Link
              to="/platform"
              className="h-11 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2 hover:opacity-90 transition-opacity"
            >
              <ShieldCheck className="h-4 w-4" />
              {t('profile.adminOpen')}
            </Link>
          </div>,
        )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-4">
        <button
          type="button"
          onClick={() => void logout()}
          className="h-11 rounded-xl border border-border bg-card text-sm font-medium flex items-center justify-center gap-2 hover:bg-muted transition-colors"
        >
          <LogOut className="h-4 w-4" />
          {t('common.logout')}
        </button>
      </div>

      <ConfirmDialog
        open={provisionOpen}
        title={t('profile.provisionNumber')}
        confirmLabel={t('profile.provisionNumber')}
        message={t('profile.provisionNumberConfirm')}
        onCancel={() => setProvisionOpen(false)}
        onConfirm={() => {
          setProvisionOpen(false);
          provision.mutate();
        }}
      />

      {billing.data && (
        <PlanDialog
          open={plansOpen}
          onClose={() => setPlansOpen(false)}
          plans={billing.data.plans}
          currentPlan={billing.data.subscription.plan}
          usage={billing.data.usage}
          currentLimits={billing.data.limits}
          currency={billing.data.currency}
          /*
           * Never in the Play build. Checkout is a full navigation to the
           * provider's hosted page, which is the one thing Google forbids an
           * Android app to do with a subscription — see `isPlayBuild`, which is
           * narrower than `isNative` on purpose. Plans and prices still show;
           * what disappears is the button.
           */
          canBuy={billing.data.paymentsEnabled && !isPlayBuild()}
        />
      )}

      {/*
        Typing the address is the whole safeguard, so the button stays disabled
        until it matches — a confirm dialog whose destructive button is live on
        arrival is one mis-tap from a deleted business. The comparison is
        case-insensitive here and again on the server, where `emailSchema`
        lowercases both sides.
      */}
      <Modal open={deleteOpen} title={t('profile.deleteAccount')} onClose={() => setDeleteOpen(false)}>
        <div className="space-y-4">
          <p className="text-sm text-foreground">{t('profile.deleteAccountWarning')}</p>
          <Field label={t('profile.deleteAccountConfirmLabel', { email: session.user.email })}>
            <Input
              type="email"
              autoComplete="off"
              dir="ltr"
              value={deleteEmail}
              onChange={(event) => setDeleteEmail(event.target.value)}
            />
          </Field>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDeleteOpen(false)}
              className="flex-1 h-11 rounded-xl border border-border bg-card text-sm font-medium hover:bg-muted transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              disabled={
                deleteAccount.isPending ||
                deleteEmail.trim().toLowerCase() !== session.user.email.toLowerCase()
              }
              onClick={() => deleteAccount.mutate(deleteEmail.trim().toLowerCase())}
              className="flex-1 h-11 rounded-xl bg-destructive text-destructive-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {deleteAccount.isPending ? '…' : t('profile.deleteAccountConfirm')}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={cancelOpen}
        title={t('billing.cancel')}
        confirmLabel={t('billing.cancel')}
        message={t('billing.cancelConfirm')}
        onCancel={() => setCancelOpen(false)}
        onConfirm={() => {
          setCancelOpen(false);
          cancelPlan.mutate();
        }}
      />
    </form>
  );
}

/**
 * Who the account belongs to, on the accent.
 *
 * Two lines, and the client asked for exactly these two: the business's name,
 * and the half-sentence printed under it at the head of a quote. A 56px tile
 * holding a person glyph used to sit beside them — the same glyph for every
 * account, so it identified nobody — and the name was `truncate`, which on the
 * phone's 366px card cuts a long business name rather than wrapping it. The
 * shell's top bar already carries the page name, so nothing else belongs here.
 */
export function IdentityHeader({ name, slogan }: { name: string; slogan?: string | null }) {
  return (
    <div className="rounded-2xl bg-gradient-to-l from-primary to-primary/85 text-primary-foreground p-5">
      <p className="text-lg font-bold break-words">{name}</p>
      {slogan && <p className="text-sm opacity-90 break-words mt-0.5">{slogan}</p>}
    </div>
  );
}

/**
 * What the package holds, and what is left of it.
 *
 * One row per cap the server actually enforces — which is why this reads
 * `PLAN_ROWS` and not a written list of selling points. `assertCanAdd` counts
 * these three and nothing else, so a fourth line here would be a promise no
 * gate keeps; the figures themselves are `limits` and `usage` off
 * `GET /billing`, the same pair the gate compares.
 *
 * An uncapped resource still prints its counter. It used to print only "no
 * limit" — no cap, therefore no number — which meant the plan whose whole
 * selling point is an unlimited quota was the one plan that never said how much
 * of it had been used.
 */
export function PlanQuota({ limits, usage }: { limits: PlanLimits; usage: PlanUsage }) {
  const { t } = useTranslation();
  const agentCopy=useAgentCopy();
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-muted-foreground">{t('profile.planContents')}</p>
      <ul className="text-xs text-muted-foreground space-y-2">
        {PLAN_ROWS.filter(
          // A plan with no voice at all (starter) would read "0 of 0, none left",
          // which looks like a quota used up rather than a feature not included.
          ({ limitKey }) => !(limitKey === 'voiceActionsPerMonth' && limits[limitKey] === 0),
        ).map(({ usageKey, limitKey }) => {
          const limit = limits[limitKey];
          const used = usage[usageKey];
          // Never negative: a cap lowered below what has already been used
          // would otherwise report a remainder of minus three.
          const left = limit === null ? null : Math.max(0, limit - used);
          const percent = limit === null || limit === 0 ? null : Math.min(100, Math.round((used / limit) * 100));
          return (
            <li key={usageKey} className="space-y-1">
              <div className="flex items-baseline justify-between gap-2">
                <span>{usageKey==='voiceActionsThisMonth'?agentCopy('actionsUsedMonth'):t(`billing.usage.${usageKey}`)}</span>
                {/* No `ltr-nums` here. That class is `direction: ltr`, which is
                    right for a bare numeral or a price, and wrong for this: the
                    string is a Hebrew sentence with numbers in it, and forcing
                    the paragraph direction reverses the word runs. "4 מתוך 20 ·
                    נותרו 16" was reaching the screen as "16 4 נותרו · 20 מתוך".
                    Hebrew laid out right-to-left already orders the digits
                    left-to-right on its own — bidi does that for free. */}
                <span className="shrink-0">
                  {limit === null
                    ? `${t('profile.planUsed', { used })} · ${t('billing.unlimited')}`
                    : `${t('billing.usageOf', { used, limit })} · ${t('billing.usageLeft', { left })}`}
                </span>
              </div>
              {percent !== null && (
                <div
                  className="h-1 rounded-full bg-muted overflow-hidden"
                  role="progressbar"
                  aria-label={usageKey==='voiceActionsThisMonth'?agentCopy('actionsUsedMonth'):t(`billing.usage.${usageKey}`)}
                  aria-valuenow={percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className={cn('h-full rounded-full', left === 0 ? 'bg-destructive' : 'bg-primary')}
                    style={{ width: `${percent}%` }}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The three preferences that belong to the business rather than to this device:
 * the currency its prices are written in, and how long each kind of document
 * may go unanswered before a follow-up reminder writes itself.
 *
 * They were fields 7, 9 and 10 of the subscriber card, read-only until its
 * pencil was pressed — filed under one person's contact details, which is not
 * where anyone looks for a setting. Here they read like the rest of the
 * settings tab: live on arrival, and saved by the button that tab already has.
 *
 * Every control is driven from outside, which is what lets the page keep one
 * form: the values are the profile form's, and so are the errors the server and
 * the schema hand back.
 */
export function BusinessPreferences({
  values,
  errors,
  onChange,
}: {
  values: { currency: string; quoteReminderHours: string; signatureReminderHours: string };
  errors: { quoteReminderHours?: string; signatureReminderHours?: string };
  onChange: (name: 'currency' | 'quoteReminderHours' | 'signatureReminderHours', value: string) => void;
}) {
  const { t } = useTranslation();

  /*
   * QUO-012 and SIG-004 ask the same question of two different clocks, so they
   * are one function with two ceilings. A price going unanswered is a sales
   * clock and an unsigned safety form is a paperwork one — and the link each
   * hangs off lives 7 days against 30, which is where the maxima come from.
   */
  const window = (name: 'quoteReminderHours' | 'signatureReminderHours', max: number) => {
    const error = errors[name];
    return (
      <Field
        label={t(`profile.${name}`)}
        htmlFor={`p-${name}`}
        hint={t(`profile.${name}Hint`)}
        {...(error ? { error: t(error) } : {})}
      >
        <Input
          id={`p-${name}`}
          type="number"
          min={1}
          max={max}
          dir="ltr"
          value={values[name]}
          invalid={Boolean(error)}
          onChange={(event) => onChange(name, event.target.value)}
        />
      </Field>
    );
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
        <Bell className="h-3.5 w-3.5" />
        {t('profile.remindersAndCurrency')}
      </p>
      <Field label={t('profile.currency')} htmlFor="p-currency">
        <Select id="p-currency" value={values.currency} onChange={(value) => onChange('currency', value)}>
          {CURRENCIES.map((option) => (
            <option key={option} value={option}>
              {t('currencies.description', { code: option, name: t(`currencies.${option}`) })}
            </option>
          ))}
        </Select>
      </Field>
      {window('quoteReminderHours', 168)}
      {window('signatureReminderHours', 720)}
    </div>
  );
}

/**
 * The month's headline figures, on the account screen.
 *
 * Four numbers, not fourteen: this card exists so an owner who opened their
 * account for something else leaves knowing how the month is going, and the
 * full breakdown is a tap away on the screen built for it. It reads the same
 * `['reports', 'summary', 'month']` query that screen does, so opening one
 * after the other costs a single request.
 */
function ReportsSnapshot() {
  const { t } = useTranslation();
  const summary = useQuery({
    queryKey: ['reports', 'summary', 'month'],
    queryFn: () => api.get<ReportSummary>('/reports/summary?range=month'),
  });

  if (summary.isLoading) {
    return (
      <div className="py-4 flex justify-center">
        <Spinner />
      </div>
    );
  }
  if (!summary.data) return <ErrorNotice code="errors.unknown" />;

  const { quotes, inquiries, calls } = summary.data;
  const figures = [
    { label: t('reports.quotesSent'), value: quotes.sent },
    { label: t('reports.quotesApproved'), value: quotes.approved },
    { label: t('reports.totalInquiries'), value: inquiries.total },
    { label: t('reports.totalCalls'), value: calls.total },
  ];

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground">{t('profile.reportsRange')}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {figures.map((figure) => (
          <div key={figure.label} className="rounded-xl bg-muted/60 p-3 text-center">
            <p className="text-xl font-bold text-primary ltr-nums leading-tight">{figure.value}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{figure.label}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-muted/40 p-3 flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">{t('reports.conversionRate')}</span>
        <span className="text-sm font-semibold text-foreground ltr-nums">
          {Math.round(quotes.conversionRate * 100)}%
        </span>
      </div>
      <Link
        to="/reports"
        className="h-11 px-4 rounded-xl border border-border bg-card text-sm font-medium flex items-center justify-center gap-2 hover:bg-muted transition-colors"
      >
        <BarChart3 className="h-4 w-4" />
        {t('profile.reportsOpen')}
      </Link>
    </div>
  );
}
